import { useLanguage, type Language } from "./useLanguage";

export function pickLocalized(
  language: Language,
  spanish: string | null | undefined,
  english: string,
  portuguese?: string | null | undefined,
): string {
  if (language === "pt") {
    const trimmed = portuguese?.trim();
    if (trimmed) return trimmed;
    return english;
  }
  if (language === "es") {
    const trimmed = spanish?.trim();
    if (trimmed) return trimmed;
    return english;
  }
  return english;
}

export function pickLocalizedList(
  language: Language,
  spanish: string[] | null | undefined,
  english: string[],
  portuguese?: string[] | null | undefined,
): string[] {
  if (language === "pt") {
    if (portuguese && portuguese.length > 0) return portuguese;
    return english;
  }
  if (language === "es") {
    if (spanish && spanish.length > 0) return spanish;
    return english;
  }
  return english;
}

export const translations = {
  en: {
    nav_courses: "Courses",
    nav_library: "Library",
    nav_consultations: "1-on-1 Consultations",
    nav_profile: "Profile",
    nav_login: "Log in",
    nav_logout: "Log out",
    nav_language: "Language / Idioma",
    nav_report: "My Report",
    skip_to_content: "Skip to content",
    footer_rights: "All rights reserved.",

    catalog_title: "Your Learning Path",
    catalog_subtitle: "Access your daily interactive child psychology challenges and resources.",
    catalog_start: "Start learning",
    catalog_completed: "Completed",
    catalog_in_progress: "In progress",
    catalog_not_started: "Get started",
    catalog_day: "Day",
    catalog_days: "Days",
    catalog_duration: "mins",

    course_start: "Start Course",
    course_resume: "Resume",
    course_go_back: "Back to all courses",
    course_overview: "Course Overview",
    course_curriculum: "Curriculum",
    course_review: "Review",
    course_continue: "Continue",
    course_start_cta: "Start",
    course_view_details: "View details",
    course_yrs: "yrs",
    course_progress: "Your Progress",
    course_complete_label: "Complete",
    course_continue_learning: "Continue Learning",
    course_includes: "Course Includes",
    course_complete_count_label: "complete",
    course_available_purchase: "Available with purchase",

    filters_button: "Filters",
    filters_age_group: "Age Group",
    filters_thematic: "Thematic",
    filters_from: "From",
    filters_to: "To",
    filters_clear_all: "Clear All",
    filters_apply: "Apply",
    filters_active: "active filters",
    filters_age_whole: "Please enter a whole number.",
    filters_age_bounds: "Age must be between 0 and 18.",
    filters_age_order: "From must be ≤ To.",
    filters_no_match: "No courses match your filters.",
    filters_clear: "Clear filters",
    catalog_empty: "No courses yet — check back soon.",
    catalog_error: "Something went wrong on our end. Try again in a moment.",
    catalog_refresh: "Refresh page",
    catalog_loading: "Loading courses",
    catalog_failed: "Failed to load courses",

    theme_adhd: "ADHD",
    theme_sleep: "Sleep",
    theme_screen_time: "Screen Time",

    profile_title: "Your Profile",
    profile_details: "Account details and preferences",
    profile_name: "Full Name",
    profile_email: "Email Address",
    profile_language: "Active Language",
    profile_sign_out: "Sign out",
    profile_active_sub: "Active Subscription",
    profile_loading: "Loading your account…",
    profile_logged_out: "You're logged out.",
    profile_logout_failed: "Logout failed. Try again.",
    profile_active_sub_desc: "You have an active subscription",
    profile_signing_out: "Signing out…",
    profile_coach_sub: "Coach Access",
    profile_expires_at: "Expires at",
    mm_profile_title: "Profile",
    mm_back: "Back",
    mm_email: "Email",
    mm_manage_sub: "Manage subscription",
    mm_log_out: "Log out",
    mm_current_plan: "Your current plan",
    mm_status_active: "Active",
    mm_status_canceled: "Cancelled",
    mm_price: "Price",
    mm_next_billing: "Next billing date",
    mm_access_until: "Access until",
    mm_cancel_sub: "Cancel subscription",
    mm_no_subs: "You don't have an active subscription.",
    mm_subs_error: "We couldn't load your subscription. Try again in a moment.",
    mm_day: "day",
    mm_days: "days",
    mm_week: "week",
    mm_weeks: "weeks",
    mm_month: "month",
    mm_months: "months",
    mm_year: "year",
    mm_years: "years",
    mm_step_1: "Step 1 of 2",
    mm_step_2: "Step 2 of 2",
    mm_back_to_sub: "Back to subscription",
    mm_sorry_title: "We're sorry to see you go",
    mm_sorry_sub: "Tell us why you're cancelling — it helps us make MindfulMe better.",
    mm_main_reason: "Main reason",
    mm_reason_too_expensive: "It's too expensive",
    mm_reason_not_using: "I'm not using it enough",
    mm_reason_got_what_needed: "I got what I needed",
    mm_reason_content_not_right: "The content isn't right for me",
    mm_reason_technical_problems: "Technical problems",
    mm_reason_something_else: "Something else",
    mm_anything_else: "Anything else you'd like to share? (optional)",
    mm_continue_cancel: "Continue to cancel",
    mm_keep_sub: "Keep subscription",
    mm_select_reason: "Please choose a reason.",
    mm_confirm_title: "Are you sure?",
    mm_confirm_text: "Your subscription will stay active until {date}. After that you won't be charged again.",
    mm_cancelling: "Cancelling…",
    mm_cancel_error: "Something went wrong. Please try again.",
    mm_cancelled_title: "Your subscription is cancelled",
    mm_cancelled_text: "You won't be charged again. You still have full access until {date}.",
    mm_back_to_profile: "Back to profile",
    mm_plan_main: "Personalized Love Report",
    mm_plan_coach: "1-on-1 Coaching",

    library_title: "Resource Library",
    library_subtitle: "Explore our collection of articles, insights, and guides.",
    library_search: "Search articles...",
    library_read_more: "Read article",
    library_no_results: "No articles found.",
    library_tag_all: "All",
    library_empty_title: "No resources yet",
    library_empty_desc: "Check back soon — new content is added regularly.",
    library_no_results_title: "No matching resources",
    library_no_results_desc: "Try adjusting your search or filter criteria.",

    type_video: "Video",
    type_article: "Article",
    type_guide: "Guide",
    type_worksheet: "Worksheet",

    verb_watch: "Watch",
    verb_read: "Read",
    verb_download: "Download",

    consultations_title: "1-on-1 Consultations",
    consultations_subtitle: "Schedule private sessions with our child behavior specialists.",
    consultations_book: "Book a Session",
    consultations_upcoming: "Upcoming Sessions",
    consultations_no_sessions: "No sessions booked yet.",
    chat_welcome: "Welcome to 1-on-1 Chat!",
    chat_welcome_desc: "Send your first message to introduce yourself and share your current parenting goals or challenges.",
    chat_placeholder: "Type your message to Emily...",

    player_next: "Next",
    player_prev: "Previous",
    player_prev_lesson: "Previous Lesson",
    player_complete_continue: "Complete and Continue",
    player_mark_complete: "Mark Complete",
    player_completed: "Completed",
    player_back_to_course: "Back to Course",
    player_download: "Download resource",
    player_listen: "Listen to audio",

    quiz_title: "Challenge Quiz",
    quiz_question: "Question",
    quiz_submit: "Submit Answer",
    quiz_next_question: "Next Question",
    quiz_retry: "Try Again",
    quiz_correct: "Correct!",
    quiz_incorrect: "Incorrect",
    quiz_completed: "Quiz Completed!",
    quiz_score: "Your Score",
    quiz_out_of: "out of",
    quiz_close: "Close Quiz",

    materials_title: "Materials",
    materials_no_file: "No file attached yet.",
    materials_unavailable: "Materials unavailable",
    materials_unreachable: "The file isn't reachable right now. You can mark this complete to continue.",
    materials_open_pdf: "Open PDF",
    materials_download: "Download",
    materials_safari_warning: "PDFs open in a new tab on mobile Safari.",
    materials_new_tab: "Opens in a new tab",

    sidebar_lesson: "Lesson",
    sidebar_bonus_lesson: "Bonus Lesson",
    sidebar_quiz: "Quiz",
    sidebar_materials: "Materials",
    sidebar_audio: "Audio",
    player_subheading_of: "of",

    quiz_saving: "Saving...",
    quiz_back: "‹ Back",
    quiz_check: "Check",
    quiz_continue: "Continue ›",

    lesson_error_title: "Couldn't load video",
    lesson_error_desc: "We tried to refresh the playback URL but it didn't work. Refresh the page, or try a different lesson.",
    lesson_error_refresh: "Refresh",

    audio_no_file: "Audio not yet uploaded.",
    audio_error_title: "Couldn't load audio",
    audio_error_desc: "The audio URL isn't reachable right now. Refresh the page, or mark this complete to continue.",

    login_title: "Log in to Kidology",
    login_desc: "Use your Kidology app to generate a magic link to access your courses.",
    login_no_link: "Don't have a magic link?",
    login_enter_email: "Enter your email below and we send you a new link",
    login_success: "If you purchased the courses, a login link has been sent to your email.",
    login_error_user_not_found: "User with this email was not found.",
    login_error_token_not_found: "No login link has been issued for this email.",
    login_sending: "Sending...",
    login_send_btn: "Send magic link",
    login_email_label: "Email",
    login_placeholder: "name@example.com",
    auth_logging_in: "Logging you in…",
    auth_failed_title: "This link doesn't work",
    auth_failed_desc: "It may have expired or already been used. Request a new one to continue.",
    error_boundary_title: "Something went wrong",
    error_boundary_desc: "We hit an unexpected error. Try reloading the page — if it keeps happening, please get in touch.",
    error_boundary_retry: "Try again",
    error_boundary_reload: "Reload page",
  },
  es: {
    nav_courses: "Cursos",
    nav_library: "Biblioteca",
    nav_consultations: "Consultas 1 a 1",
    nav_profile: "Perfil",
    nav_login: "Iniciar sesión",
    nav_logout: "Cerrar sesión",
    nav_language: "Idioma / Language",
    nav_report: "Mi informe",
    skip_to_content: "Saltar al contenido",
    footer_rights: "Todos los derechos reservados.",

    catalog_title: "Tu Ruta de Aprendizaje",
    catalog_subtitle: "Accede a tus desafíos y recursos diarios interactivos de psicología infantil.",
    catalog_start: "Comenzar a aprender",
    catalog_completed: "Completado",
    catalog_in_progress: "En progreso",
    catalog_not_started: "Empezar",
    catalog_day: "Día",
    catalog_days: "Días",
    catalog_duration: "min",

    course_start: "Iniciar Curso",
    course_resume: "Reanudar",
    course_go_back: "Volver a todos los cursos",
    course_overview: "Descripción del Curso",
    course_curriculum: "Plan de Estudios",
    course_review: "Revisar",
    course_continue: "Continuar",
    course_start_cta: "Comenzar",
    course_view_details: "Ver detalles",
    course_yrs: "años",
    course_progress: "Tu Progreso",
    course_complete_label: "Completado",
    course_continue_learning: "Continuar Aprendiendo",
    course_includes: "El Curso Incluye",
    course_complete_count_label: "completados",
    course_available_purchase: "Disponible con la compra",

    filters_button: "Filtros",
    filters_age_group: "Grupo de edad",
    filters_thematic: "Temática",
    filters_from: "Desde",
    filters_to: "Hasta",
    filters_clear_all: "Borrar todo",
    filters_apply: "Aplicar",
    filters_active: "filtros activos",
    filters_age_whole: "Introduce un número entero.",
    filters_age_bounds: "La edad debe estar entre 0 y 18.",
    filters_age_order: "Desde debe ser ≤ Hasta.",
    filters_no_match: "Ningún curso coincide con tus filtros.",
    filters_clear: "Borrar filtros",
    catalog_empty: "Aún no hay cursos — vuelve pronto.",
    catalog_error: "Algo salió mal. Inténtalo de nuevo en un momento.",
    catalog_refresh: "Actualizar página",
    catalog_loading: "Cargando cursos",
    catalog_failed: "No se pudieron cargar los cursos",

    theme_adhd: "TDAH",
    theme_sleep: "Sueño",
    theme_screen_time: "Tiempo de pantalla",

    profile_title: "Tu Perfil",
    profile_details: "Detalles y preferencias de la cuenta",
    profile_name: "Nombre Completo",
    profile_email: "Correo Electrónico",
    profile_language: "Idioma Activo",
    profile_sign_out: "Cerrar sesión",
    profile_active_sub: "Suscripción Activa",
    profile_loading: "Cargando tu cuenta…",
    profile_logged_out: "Has cerrado sesión.",
    profile_logout_failed: "Error al cerrar sesión. Intenta de nuevo.",
    profile_active_sub_desc: "Tienes una suscripción activa",
    profile_signing_out: "Cerrando sesión…",
    profile_coach_sub: "Acceso a Coach",
    profile_expires_at: "Vence el",
    mm_profile_title: "Perfil",
    mm_back: "Volver",
    mm_email: "Correo electrónico",
    mm_manage_sub: "Gestionar suscripción",
    mm_log_out: "Cerrar sesión",
    mm_current_plan: "Tu plan actual",
    mm_status_active: "Activa",
    mm_status_canceled: "Cancelada",
    mm_price: "Precio",
    mm_next_billing: "Próximo cobro",
    mm_access_until: "Acceso hasta",
    mm_cancel_sub: "Cancelar suscripción",
    mm_no_subs: "No tienes una suscripción activa.",
    mm_subs_error: "No pudimos cargar tu suscripción. Inténtalo de nuevo en un momento.",
    mm_day: "día",
    mm_days: "días",
    mm_week: "semana",
    mm_weeks: "semanas",
    mm_month: "mes",
    mm_months: "meses",
    mm_year: "año",
    mm_years: "años",
    mm_step_1: "Paso 1 de 2",
    mm_step_2: "Paso 2 de 2",
    mm_back_to_sub: "Volver a la suscripción",
    mm_sorry_title: "Lamentamos que te vayas",
    mm_sorry_sub: "Cuéntanos por qué cancelas: nos ayuda a mejorar MindfulMe.",
    mm_main_reason: "Motivo principal",
    mm_reason_too_expensive: "Es demasiado caro",
    mm_reason_not_using: "No lo uso lo suficiente",
    mm_reason_got_what_needed: "Ya obtuve lo que necesitaba",
    mm_reason_content_not_right: "El contenido no es para mí",
    mm_reason_technical_problems: "Problemas técnicos",
    mm_reason_something_else: "Otro motivo",
    mm_anything_else: "¿Algo más que quieras contarnos? (opcional)",
    mm_continue_cancel: "Continuar con la cancelación",
    mm_keep_sub: "Mantener suscripción",
    mm_select_reason: "Elige un motivo.",
    mm_confirm_title: "¿Seguro que quieres cancelar?",
    mm_confirm_text: "Tu suscripción seguirá activa hasta el {date}. Después no se te volverá a cobrar.",
    mm_cancelling: "Cancelando…",
    mm_cancel_error: "Algo salió mal. Inténtalo de nuevo.",
    mm_cancelled_title: "Tu suscripción ha sido cancelada",
    mm_cancelled_text: "No se te volverá a cobrar. Tienes acceso completo hasta el {date}.",
    mm_back_to_profile: "Volver al perfil",
    mm_plan_main: "Informe de amor personalizado",
    mm_plan_coach: "Coaching 1 a 1",

    library_title: "Biblioteca de Recursos",
    library_subtitle: "Explora nuestra colección de artículos, reflexiones y guías.",
    library_search: "Buscar artículos...",
    library_read_more: "Leer artículo",
    library_no_results: "No se encontraron artículos.",
    library_tag_all: "Todos",
    library_empty_title: "Aún no hay recursos",
    library_empty_desc: "Vuelve pronto: agregamos nuevo contenido con regularidad.",
    library_no_results_title: "No hay recursos coincidentes",
    library_no_results_desc: "Intenta ajustar tu búsqueda o criterios de filtro.",

    type_video: "Video",
    type_article: "Artículo",
    type_guide: "Guía",
    type_worksheet: "Material",

    verb_watch: "Ver",
    verb_read: "Leer",
    verb_download: "Descargar",

    consultations_title: "Consultas 1 a 1",
    consultations_subtitle: "Programa sesiones privadas con nuestros especialistas en comportamiento infantil.",
    consultations_book: "Reservar una Sesión",
    consultations_upcoming: "Próximas Sesiones",
    consultations_no_sessions: "Aún no hay sesiones reservadas.",
    chat_welcome: "¡Bienvenido al Chat 1 a 1!",
    chat_welcome_desc: "Envía tu primer mensaje para presentarte y compartir tus metas o desafíos parentales actuales.",
    chat_placeholder: "Escribe tu mensaje para Emily...",

    player_next: "Siguiente",
    player_prev: "Anterior",
    player_prev_lesson: "Lección Anterior",
    player_complete_continue: "Completar y Continuar",
    player_mark_complete: "Marcar como Completado",
    player_completed: "Completado",
    player_back_to_course: "Volver al Curso",
    player_download: "Descargar recurso",
    player_listen: "Escuchar audio",

    quiz_title: "Cuestionario del Desafío",
    quiz_question: "Pregunta",
    quiz_submit: "Enviar Respuesta",
    quiz_next_question: "Siguiente Pregunta",
    quiz_retry: "Intentar de Nuevo",
    quiz_correct: "¡Correcto!",
    quiz_incorrect: "Incorrecto",
    quiz_completed: "¡Cuestionario Completado!",
    quiz_score: "Tu Calificación",
    quiz_out_of: "de",
    quiz_close: "Cerrar Cuestionario",

    materials_title: "Materiales",
    materials_no_file: "Aún no se ha adjuntado ningún archivo.",
    materials_unavailable: "Materiales no disponibles",
    materials_unreachable: "El archivo no está accesible en este momento. Puedes marcar esto como completado para continuar.",
    materials_open_pdf: "Abrir PDF",
    materials_download: "Descargar",
    materials_safari_warning: "Los PDF se abren en una nueva pestaña en Safari para móviles.",
    materials_new_tab: "Se abre en una nueva pestaña",

    sidebar_lesson: "Lección",
    sidebar_bonus_lesson: "Lección adicional",
    sidebar_quiz: "Cuestionario",
    sidebar_materials: "Materiales",
    sidebar_audio: "Audio",
    player_subheading_of: "de",

    quiz_saving: "Guardando...",
    quiz_back: "‹ Atrás",
    quiz_check: "Comprobar",
    quiz_continue: "Continuar ›",

    lesson_error_title: "No se pudo cargar el video",
    lesson_error_desc: "Intentamos actualizar la URL de reproducción pero no funcionó. Actualiza la página o prueba con otra lección.",
    lesson_error_refresh: "Actualizar",

    audio_no_file: "Audio aún no subido.",
    audio_error_title: "No se pudo cargar el audio",
    audio_error_desc: "La URL del audio no está accesible en este momento. Actualiza la página o marca esto como completado para continuar.",

    login_title: "Iniciar sesión en Kidology",
    login_desc: "Usa tu aplicación Kidology para generar un enlace mágico para acceder a tus cursos.",
    login_no_link: "¿No tienes un enlace mágico?",
    login_enter_email: "Ingresa tu correo electrónico a continuación y te enviaremos un nuevo enlace",
    login_success: "Si compraste los cursos, se ha enviado un enlace de inicio de sesión a tu correo electrónico.",
    login_error_user_not_found: "No se encontró ningún usuario con este correo electrónico.",
    login_error_token_not_found: "No se ha emitido ningún enlace de inicio de sesión para este correo electrónico.",
    login_sending: "Enviando...",
    login_send_btn: "Enviar enlace mágico",
    login_email_label: "Correo electrónico",
    login_placeholder: "nombre@ejemplo.com",
    auth_logging_in: "Iniciando sesión…",
    auth_failed_title: "Este enlace no funciona",
    auth_failed_desc: "Puede haber expirado o ya haber sido utilizado. Solicita uno nuevo para continuar.",
    error_boundary_title: "Algo salió mal",
    error_boundary_desc: "Ocurrió un error inesperado. Intenta recargar la página; si el problema continúa, ponte en contacto con nosotros.",
    error_boundary_retry: "Intentar de nuevo",
    error_boundary_reload: "Recargar página",
  },
  pt: {
    nav_courses: "Cursos",
    nav_library: "Biblioteca",
    nav_consultations: "Consultas 1 a 1",
    nav_profile: "Perfil",
    nav_login: "Entrar",
    nav_logout: "Sair",
    nav_language: "Idioma / Language",
    nav_report: "Meu relatório",
    skip_to_content: "Pular para o conteúdo",
    footer_rights: "Todos os direitos reservados.",

    catalog_title: "Sua Jornada de Aprendizado",
    catalog_subtitle: "Acesse seus desafios e recursos diários interativos de psicologia infantil.",
    catalog_start: "Começar a aprender",
    catalog_completed: "Concluído",
    catalog_in_progress: "Em andamento",
    catalog_not_started: "Começar",
    catalog_day: "Dia",
    catalog_days: "Dias",
    catalog_duration: "min",

    course_start: "Iniciar Curso",
    course_resume: "Continuar",
    course_go_back: "Voltar para todos os cursos",
    course_overview: "Visão Geral do Curso",
    course_curriculum: "Conteúdo Programático",
    course_review: "Avaliação",
    course_continue: "Continuar",
    course_start_cta: "Começar",
    course_view_details: "Ver detalhes",
    course_yrs: "anos",
    course_progress: "Seu Progresso",
    course_complete_label: "Concluído",
    course_continue_learning: "Continuar Aprendendo",
    course_includes: "O Curso Inclui",
    course_complete_count_label: "concluídos",
    course_available_purchase: "Disponível com a compra",

    filters_button: "Filtros",
    filters_age_group: "Faixa etária",
    filters_thematic: "Tema",
    filters_from: "De",
    filters_to: "Até",
    filters_clear_all: "Limpar tudo",
    filters_apply: "Aplicar",
    filters_active: "filtros ativos",
    filters_age_whole: "Digite um número inteiro.",
    filters_age_bounds: "A idade deve estar entre 0 e 18 anos.",
    filters_age_order: "O valor 'De' deve ser ≤ 'Até'.",
    filters_no_match: "Nenhum curso corresponde aos seus filtros.",
    filters_clear: "Limpar filtros",
    catalog_empty: "Nenhum curso disponível ainda — volte em breve.",
    catalog_error: "Ocorreu um erro. Tente novamente em alguns instantes.",
    catalog_refresh: "Atualizar página",
    catalog_loading: "Carregando cursos",
    catalog_failed: "Falha ao carregar cursos",

    theme_adhd: "TDAH",
    theme_sleep: "Sono",
    theme_screen_time: "Tempo de tela",

    profile_title: "Seu Perfil",
    profile_details: "Detalhes e preferências da conta",
    profile_name: "Nome Completo",
    profile_email: "Endereço de E-mail",
    profile_language: "Idioma Ativo",
    profile_sign_out: "Sair",
    profile_active_sub: "Assinatura Ativa",
    profile_loading: "Carregando sua conta…",
    profile_logged_out: "Você saiu da sua conta.",
    profile_logout_failed: "Falha ao sair. Tente novamente.",
    profile_active_sub_desc: "Você tem uma assinatura ativa",
    profile_signing_out: "Saindo…",
    profile_coach_sub: "Acesso ao Coach",
    profile_expires_at: "Expira em",
    mm_profile_title: "Perfil",
    mm_back: "Voltar",
    mm_email: "E-mail",
    mm_manage_sub: "Gerenciar assinatura",
    mm_log_out: "Sair",
    mm_current_plan: "Seu plano atual",
    mm_status_active: "Ativa",
    mm_status_canceled: "Cancelada",
    mm_price: "Preço",
    mm_next_billing: "Próxima cobrança",
    mm_access_until: "Acesso até",
    mm_cancel_sub: "Cancelar assinatura",
    mm_no_subs: "Você não tem uma assinatura ativa.",
    mm_subs_error: "Não conseguimos carregar sua assinatura. Tente novamente em instantes.",
    mm_day: "dia",
    mm_days: "dias",
    mm_week: "semana",
    mm_weeks: "semanas",
    mm_month: "mês",
    mm_months: "meses",
    mm_year: "ano",
    mm_years: "anos",
    mm_step_1: "Etapa 1 de 2",
    mm_step_2: "Etapa 2 de 2",
    mm_back_to_sub: "Voltar para a assinatura",
    mm_sorry_title: "Sentimos muito por você ir embora",
    mm_sorry_sub: "Conte por que está cancelando: isso nos ajuda a melhorar o MindfulMe.",
    mm_main_reason: "Motivo principal",
    mm_reason_too_expensive: "É caro demais",
    mm_reason_not_using: "Não estou usando o suficiente",
    mm_reason_got_what_needed: "Já consegui o que precisava",
    mm_reason_content_not_right: "O conteúdo não é para mim",
    mm_reason_technical_problems: "Problemas técnicos",
    mm_reason_something_else: "Outro motivo",
    mm_anything_else: "Quer nos contar mais alguma coisa? (opcional)",
    mm_continue_cancel: "Continuar cancelamento",
    mm_keep_sub: "Manter assinatura",
    mm_select_reason: "Escolha um motivo.",
    mm_confirm_title: "Tem certeza?",
    mm_confirm_text: "Sua assinatura continuará ativa até {date}. Depois disso, você não será cobrado novamente.",
    mm_cancelling: "Cancelando…",
    mm_cancel_error: "Algo deu errado. Tente novamente.",
    mm_cancelled_title: "Sua assinatura foi cancelada",
    mm_cancelled_text: "Você não será cobrado novamente. Você tem acesso completo até {date}.",
    mm_back_to_profile: "Voltar ao perfil",
    mm_plan_main: "Relatório de amor personalizado",
    mm_plan_coach: "Coaching individual",

    library_title: "Biblioteca de Recursos",
    library_subtitle: "Explore nossa coleção de artigos, percepções e guias.",
    library_search: "Buscar artigos...",
    library_read_more: "Ler artigo",
    library_no_results: "Nenhum artigo encontrado.",
    library_tag_all: "Todos",
    library_empty_title: "Nenhum recurso ainda",
    library_empty_desc: "Volte em breve — adicionamos novos conteúdos regularmente.",
    library_no_results_title: "Nenhum recurso correspondente",
    library_no_results_desc: "Tente ajustar sua busca ou critérios de filtro.",

    type_video: "Vídeo",
    type_article: "Artigo",
    type_guide: "Guia",
    type_worksheet: "Material",

    verb_watch: "Assistir",
    verb_read: "Ler",
    verb_download: "Baixar",

    consultations_title: "Consultas 1 a 1",
    consultations_subtitle: "Agende sessões individuais com nossos especialistas em comportamento infantil.",
    consultations_book: "Agendar uma Sessão",
    consultations_upcoming: "Próximas Sessões",
    consultations_no_sessions: "Nenhuma sessão agendada ainda.",
    chat_welcome: "Boas-vindas ao Chat 1 a 1!",
    chat_welcome_desc: "Envie sua primeira mensagem para se apresentar e compartilhar seus objetivos ou desafios atuais na criação dos filhos.",
    chat_placeholder: "Digite sua mensagem para Emily...",

    player_next: "Próximo",
    player_prev: "Anterior",
    player_prev_lesson: "Lição Anterior",
    player_complete_continue: "Concluir e Continuar",
    player_mark_complete: "Marcar como Concluído",
    player_completed: "Concluído",
    player_back_to_course: "Voltar ao Curso",
    player_download: "Baixar recurso",
    player_listen: "Ouvir áudio",

    quiz_title: "Quiz do Desafio",
    quiz_question: "Pergunta",
    quiz_submit: "Enviar Resposta",
    quiz_next_question: "Próxima Pergunta",
    quiz_retry: "Tentar Novamente",
    quiz_correct: "Correto!",
    quiz_incorrect: "Incorreto",
    quiz_completed: "Quiz Concluído!",
    quiz_score: "Sua Pontuação",
    quiz_out_of: "de",
    quiz_close: "Fechar Quiz",

    materials_title: "Materiais",
    materials_no_file: "Nenhum arquivo anexado ainda.",
    materials_unavailable: "Materiais indisponíveis",
    materials_unreachable: "O arquivo não está acessível no momento. Você pode marcar como concluído para continuar.",
    materials_open_pdf: "Abrir PDF",
    materials_download: "Baixar",
    materials_safari_warning: "Os PDFs abrem em uma nova aba no Safari móvel.",
    materials_new_tab: "Abre em uma nova aba",

    sidebar_lesson: "Lição",
    sidebar_bonus_lesson: "Lição bônus",
    sidebar_quiz: "Quiz",
    sidebar_materials: "Materiais",
    sidebar_audio: "Áudio",
    player_subheading_of: "de",

    quiz_saving: "Salvando...",
    quiz_back: "‹ Voltar",
    quiz_check: "Verificar",
    quiz_continue: "Continuar ›",

    lesson_error_title: "Não foi possível carregar o vídeo",
    lesson_error_desc: "Tentamos atualizar a URL de reprodução, mas não funcionou. Atualize a página ou tente outra lição.",
    lesson_error_refresh: "Atualizar",

    audio_no_file: "Áudio ainda não enviado.",
    audio_error_title: "Não foi possível carregar o áudio",
    audio_error_desc: "A URL do áudio não está acessível no momento. Atualize a página ou marque como concluído para continuar.",

    login_title: "Entrar na Kidology",
    login_desc: "Use o seu aplicativo Kidology para gerar um link mágico para acessar seus cursos.",
    login_no_link: "Não tem um link mágico?",
    login_enter_email: "Digite seu e-mail abaixo e enviaremos um novo link",
    login_success: "Se você comprou os cursos, um link de acesso foi enviado para o seu e-mail.",
    login_error_user_not_found: "Usuário com este e-mail não foi encontrado.",
    login_error_token_not_found: "Nenhum link de acesso foi emitido para este e-mail.",
    login_sending: "Enviando...",
    login_send_btn: "Enviar link mágico",
    login_email_label: "E-mail",
    login_placeholder: "nome@exemplo.com",
    auth_logging_in: "Entrando…",
    auth_failed_title: "Este link não funciona",
    auth_failed_desc: "Ele pode ter expirado ou já ter sido utilizado. Solicite um novo link para continuar.",
    error_boundary_title: "Algo deu errado",
    error_boundary_desc: "Ocorreu um erro inesperado. Tente recarregar a página; se o problema persistir, entre em contato conosco.",
    error_boundary_retry: "Tentar novamente",
    error_boundary_reload: "Recarregar página",
  },
} as const;

export type TranslationKeys = keyof typeof translations.en;

const THEME_I18N_KEYS: Record<string, TranslationKeys> = {
  ADHD: "theme_adhd",
  Sleep: "theme_sleep",
  "Screen Time": "theme_screen_time",
};

export function translateTheme(
  t: (key: TranslationKeys) => string,
  theme: string,
): string {
  const key = THEME_I18N_KEYS[theme];
  return key ? t(key) : theme;
}

export function translateTag(
  t: (key: TranslationKeys) => string,
  tag: string,
): string {
  const key = THEME_I18N_KEYS[tag];
  return key ? t(key) : tag;
}

export function translateLibraryType(
  t: (key: TranslationKeys) => string,
  type: string,
): string {
  switch (type) {
    case "Video": return t("type_video");
    case "Article": return t("type_article");
    case "Guide": return t("type_guide");
    case "Worksheet": return t("type_worksheet");
    default: return type;
  }
}

export function translateLibraryVerb(
  t: (key: TranslationKeys) => string,
  type: string,
): string {
  switch (type) {
    case "Video": return t("verb_watch");
    case "Article":
    case "Guide": return t("verb_read");
    case "Worksheet": return t("verb_download");
    default: return "";
  }
}

export function useTranslation() {
  const { language } = useLanguage();
  
  const t = (key: TranslationKeys): string => {
    return translations[language][key] || translations.en[key] || key;
  };

  return { t, language };
}
